// SmartScheduler.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API, { matchTeachers } from './api';

/** ---------- Helpers ---------- */
function normalizeTime(val) {
  if (!val) return '';
  let t = val;
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}
function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`;
}
function addMinutes(time, mins) {
  return minutesToHHMM(toMinutes(time) + mins);
}
function simplifyIntervals(list = []) {
  return (list || []).map(x => ({
    from: normalizeTime(x.from_time ?? x.from),
    to: normalizeTime(x.to_time ?? x.to),
  })).filter(i => toMinutes(i.to) > toMinutes(i.from));
}
function subtractIntervals(base = [], blocked = []) {
  let result = simplifyIntervals(base);
  const bad = simplifyIntervals(blocked);
  for (const exc of bad) {
    const excFrom = toMinutes(exc.from), excTo = toMinutes(exc.to);
    const next = [];
    for (const it of result) {
      const a = toMinutes(it.from), b = toMinutes(it.to);
      if (excTo <= a || excFrom >= b) next.push(it);
      else {
        if (excFrom > a) next.push({ from: it.from, to: normalizeTime(exc.from) });
        if (excTo   < b) next.push({ from: normalizeTime(exc.to), to: it.to });
      }
    }
    result = next;
  }
  return result;
}
function intersectTwoSets(A = [], B = []) {
  const res = [];
  const a = simplifyIntervals(A);
  const b = simplifyIntervals(B);
  for (const i of a) {
    for (const j of b) {
      const from = Math.max(toMinutes(i.from), toMinutes(j.from));
      const to   = Math.min(toMinutes(i.to),   toMinutes(j.to));
      if (to > from) res.push({ from: minutesToHHMM(from), to: minutesToHHMM(to) });
    }
  }
  return mergeTouching(res);
}
function mergeTouching(intervals = []) {
  const sorted = intervals.slice().sort((x,y)=>toMinutes(x.from)-toMinutes(y.from));
  const out = [];
  for (const it of sorted) {
    if (!out.length) out.push({...it});
    else {
      const last = out[out.length-1];
      if (toMinutes(it.from) <= toMinutes(last.to)) {
        if (toMinutes(it.to) > toMinutes(last.to)) last.to = it.to;
      } else out.push({...it});
    }
  }
  return out;
}
function clampTo(i, startM, endM) {
  const f = Math.max(toMinutes(i.from), startM);
  const t = Math.min(toMinutes(i.to),   endM);
  if (t <= f) return null;
  return { from: minutesToHHMM(f), to: minutesToHHMM(t) };
}

// cena slota
function computeSlotTotal(from, to, teacher = {}) {
  const mins = toMinutes(to) - toMinutes(from);
  const hours = Math.max(0, mins) / 60;
  const hourly = Number(teacher.hourly_rate ?? 0);
  const travel = Number(teacher.travel_cost ?? 0);
  const bonus  = Number(teacher.bonus ?? 0);
  const total = hourly * hours + travel + bonus;
  return Math.round(total * 100) / 100;
}

/** ---------- Timeline config ---------- */
const SNAP_MIN  = 5;
const MIN_DURATION_MIN = 30;
const MAX_STANDARD_MIN = 45;

/** Boje po teacher-u */
const COLOR_PALETTE = [
  'bg-emerald-400', 'bg-sky-400', 'bg-fuchsia-400',
  'bg-amber-400', 'bg-lime-400', 'bg-rose-400',
  'bg-cyan-400', 'bg-violet-400', 'bg-orange-400'
];
function colorForTeacher(id) {
  if (!id && id !== 0) return 'bg-gray-400';
  const idx = Math.abs(parseInt(id, 10)) % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx];
}
function initials(name='') {
  const parts = name.trim().split(/\s+/);
  return parts.slice(0,2).map(p=>p[0]?.toUpperCase()||'').join('');
}

/** ---------- ID helpers (id vs teacher_id) ---------- */
const getTid = (t) => String(t?.id ?? t?.teacher_id ?? t?.teacher?.id ?? '');
const getTidNum = (tOrId) => {
  if (tOrId == null) return null;
  const n = typeof tOrId === 'object' ? (tOrId.id ?? tOrId.teacher_id ?? tOrId.teacher?.id) : tOrId;
  const parsed = Number(n);
  return Number.isFinite(parsed) ? parsed : null;
};
const normalizeTeacher = (t) => {
  if (!t) return null;
  const id = t.id ?? t.teacher_id ?? t.teacher?.id;
  return { id, ...t };
};

/** ---------- Badge helpers ---------- */
function TypeBadge({ type }) {
  const isEvent = type === 'event';
  const cls = isEvent
    ? 'bg-yellow-100 text-orange-800'
    : 'bg-green-100 text-green-800';
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>
      {isEvent ? 'Event' : 'Standard'}
    </span>
  );
}
function StatusBadge({ status }) {
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    complete: 'bg-green-200 text-green-900',
    canceled: 'bg-purple-200 text-purple-700',
  };
  const cls = map[status] || 'bg-gray-200 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{status || '—'}</span>;
}
function ReassignedBadge({ reassigned }) {
  const map = {
    pending: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  if (!reassigned || !map[reassigned]) return null;
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${map[reassigned]}`}>
      Substitute: {reassigned}
    </span>
  );
}

/** ---------- Toast (efemerna poruka) ---------- */
function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
}

/** ---------- Component ---------- */
export default function SmartScheduler() {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [overlayTeacherId, setOverlayTeacherId] = useState('');
  const [studentDefaultTeacherId, setStudentDefaultTeacherId] = useState(null);

  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  const [studentAvailByDate, setStudentAvailByDate] = useState({});
  const [teacherAvailByDate, setTeacherAvailByDate] = useState({});
  const [teacherFreeCache, setTeacherFreeCache] = useState({});

  const [slots, setSlots] = useState({});
  const [serverDateStr, setServerDateStr] = useState('');
  const [useORS, setUseORS] = useState(true);
  const [status, setStatus] = useState(''); // ide kroz Toast

  const [matchByTeacherId, setMatchByTeacherId] = useState({});

  // modal & teacher suggestions
  const [showModal, setShowModal] = useState(false);
  const [modalDate, setModalDate] = useState('');
  const [modalFrom, setModalFrom] = useState('');
  const [modalMaxTo, setModalMaxTo] = useState('');
  const [isEvent, setIsEvent] = useState(false);
  const [eventEndTime, setEventEndTime] = useState('');
  const [teacherOptions, setTeacherOptions] = useState({
    default_teacher: null,
    alternatives: [],
    orsMatrixStatus: 'SKIPPED',
    resolved_location: null,
    programRequired: null,
  });

  // drag
  const [drag, setDrag] = useState(null);
  const trackRefs = useRef({});

  // --- Dynamic workday (LOAD ONCE) ---
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });

  useEffect(() => {
    API.get('/workday-settings')
      .then(res => setWorkday({
        start: (res.data?.start || '08:00').slice(0,5),
        end:   (res.data?.end   || '18:00').slice(0,5),
      }))
      .catch(()=>{ /* silent */ });
  }, []);

  // --- Derivatives from dynamic workday ---
  const DAY_START = workday.start;
  const DAY_END   = workday.end;
  const dayStartM = toMinutes(DAY_START);
  const dayEndM   = toMinutes(DAY_END);
  const spanM     = Math.max(1, dayEndM - dayStartM);

  // --- Local pctBox that sees dynamic values ---
  const pctBox = (i) => {
    const left = ((toMinutes(i.from) - dayStartM) / spanM) * 100;
    const width = ((toMinutes(i.to) - toMinutes(i.from)) / spanM) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(0, Math.min(100 - left, width))}%`
    };
  };

  // Toast auto-clear
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(''), 3000);
    return () => clearTimeout(t);
  }, [status]);
  useEffect(() => { setStatus(''); }, [selectedStudent, selectedWeekStart]);

  // initial load
  useEffect(() => {
    API.get('/server-time').then(res => setServerDateStr(res.data.serverTime.slice(0,10)));
    API.get('/admin/students?assigned_only=0').then(res => setStudents(res.data || []));
    API.get('/teachers').then(res => setTeachers(res.data || []));
    API.get('/weeks').then(res => setWeekOptions(res.data || []));
  }, []);

  // default teacher for selected student
  useEffect(() => {
    if (!selectedStudent) {
      setStudentDefaultTeacherId(null);
      setOverlayTeacherId('');
      return;
    }
    const st = students.find(s => String(s.id) === String(selectedStudent));
    const defT = st?.default_teacher_id || null;
    setStudentDefaultTeacherId(defT);
    setOverlayTeacherId(defT ? String(defT) : '');
  }, [selectedStudent, students]);

  // week dates
  useEffect(() => {
    if (!selectedWeekStart) return;
    API.get(`/week-dates/${selectedWeekStart}`).then(res => setWeekDates(res.data || []));
  }, [selectedWeekStart]);

  // reset teacherFreeCache kad promeniš nedelju
  useEffect(() => { setTeacherFreeCache({}); }, [selectedWeekStart]);

  // student availability
  useEffect(() => {
    if (!selectedStudent || !selectedWeekStart) return;
    API.get(`/student-availability/realweek/${selectedStudent}/${selectedWeekStart}`)
      .then(res => {
        const map = {};
        Object.entries(res.data || {}).forEach(([date, obj]) => {
          const intervals = simplifyIntervals(obj.intervals || []);
          const blocked   = simplifyIntervals(obj.blocked || []);
          const free      = subtractIntervals(intervals, blocked);
          map[date] = { free, intervals, blocked };
        });
        setStudentAvailByDate(map);
      });
  }, [selectedStudent, selectedWeekStart]);

  // overlay teacher availability (vizuelno)
  async function refreshOverlayTeacher() {
    if (!overlayTeacherId || !selectedWeekStart) {
      setTeacherAvailByDate({});
      return;
    }
    try {
      const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${overlayTeacherId}`);
      const a = res.data?.availability || {};
      const e = res.data?.exceptions || {};
      const daysOff = res.data?.day_offs || [];
      const map = {};
      Object.keys(a).forEach(date => {
        const isDayOff = daysOff.includes(date);
        const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
        map[date] = { free };
      });
      const busyRes = await API.get(`/visits/teacher-week?teacher_id=${overlayTeacherId}&week_start=${selectedWeekStart}`);
      const busyByDate = busyRes.data || {};
      Object.keys(map).forEach(date => {
        const free = map[date].free || [];
        const busy = (busyByDate[date] || []).map(i => ({ from: i.from_time, to: i.to_time }));
        map[date].free = subtractIntervals(free, busy);
      });
      setTeacherAvailByDate(map);
    } catch {
      // silent
    }
  }
  useEffect(() => { refreshOverlayTeacher(); }, [overlayTeacherId, selectedWeekStart]);

  // cache teacher free for validation
  async function ensureTeacherWeekFree(teacherId, force = false) {
    if (!teacherId && teacherId !== 0) return;
    if (!force && teacherFreeCache[teacherId]) return;

    try {
      const res = await API.get(`/availability/week/${selectedWeekStart}?teacher_id=${teacherId}`);
      const a = res.data?.availability || {};
      const e = res.data?.exceptions || {};
      const daysOff = res.data?.day_offs || [];

      const base = {};
      Object.keys(a).forEach(date => {
        const isDayOff = daysOff.includes(date);
        const free = isDayOff ? [] : subtractIntervals(a[date], e[date] || []);
        base[date] = { free };
      });

      const busyRes = await API.get(`/visits/teacher-week?teacher_id=${teacherId}&week_start=${selectedWeekStart}`);
      const busyByDate = busyRes.data || {};
      Object.keys(base).forEach(date => {
        const free = base[date].free || [];
        const busy = (busyByDate[date] || []).map(i => ({ from: i.from_time, to: i.to_time }));
        base[date].free = subtractIntervals(free, busy);
      });

      setTeacherFreeCache(prev => ({ ...prev, [teacherId]: base }));
    } catch {
      setStatus('Failed to load teacher availability for validation.');
    }
  }
  function getTeacherFree(teacherId, date) {
    return teacherFreeCache?.[teacherId]?.[date]?.free || [];
  }

  function withinStudentFree(date, from, to) {
    const free = (studentAvailByDate[date]?.free || []);
    const f = toMinutes(from), t = toMinutes(to);
    return free.some(seg => f >= toMinutes(seg.from) && t <= toMinutes(seg.to));
  }
  function withinTeacherFree(date, from, to, teacherId) {
    if (teacherId == null) return true;
    const free = getTeacherFree(teacherId, date);
    const f = toMinutes(from), t = toMinutes(to);
    return free.some(seg => f >= toMinutes(seg.from) && t <= toMinutes(seg.to));
  }

  // ----- Coverage hints -----
  function coverageHint(freeSegments, from, to) {
    const fM = toMinutes(from), tM = toMinutes(to);
    const seg = freeSegments.find(s => toMinutes(s.from) <= fM && fM < toMinutes(s.to));
    if (seg) {
      const segTo = toMinutes(seg.to);
      if (segTo >= tM) return '';
      return `Teacher free until ${minutesToHHMM(segTo)} (needs until ${normalizeTime(to)})`;
    }
    const next = freeSegments.find(s => toMinutes(s.from) >= fM);
    if (next) return `Next free ${normalizeTime(next.from)}–${normalizeTime(next.to)}`;
    const prev = [...freeSegments].reverse().find(s => toMinutes(s.to) <= fM);
    if (prev) return `Last free ended at ${normalizeTime(prev.to)}`;
    return 'No free time this day';
  }
  function teacherCoverageHint(date, teacherId, from, to) {
    return coverageHint(getTeacherFree(teacherId, date), from, to);
  }
  function studentCoverageHint(date, from, to) {
    const free = (studentAvailByDate[date]?.free || []);
    return coverageHint(free, from, to);
  }

  // --- Suggested Teachers ---
  async function loadMatchMap(studentId) {
    try {
      const res = await matchTeachers(studentId);
      const arr = res.data?.matches || [];
      const map = {};
      arr.forEach(r => {
        const tid = String(r.teacher_id ?? r.id);
        if (!tid) return;
        map[tid] = {
          score: Number(r.score ?? 0),
          reasons: Array.isArray(r.reasons) ? r.reasons : []
        };
      });
      setMatchByTeacherId(map);
      return map;
    } catch {
      setMatchByTeacherId({});
      return {};
    }
  }
  function getMatchForTeacher(t) {
    if (!t) return null;
    return matchByTeacherId[getTid(t)] || null;
  }

  async function loadExistingVisits() {
    if (!selectedStudent || !selectedWeekStart) { setSlots({}); return; }
    try {
      const res = await API.get(`/visits/week/${selectedStudent}/${selectedWeekStart}`);
      const m = {};
      const teacherIds = new Set();
      Object.entries(res.data || {}).forEach(([date, list]) => {
        m[date] = (list || []).map(s => {
          const from = normalizeTime(s.from);
          const to   = normalizeTime(s.type === 'event' ? (s.event_end_time || s.to) : s.to);
          const tnorm = normalizeTeacher(s.teacher || null);
          if (tnorm?.id != null) teacherIds.add(getTidNum(tnorm));
          return {
            ...s,
            teacher: tnorm,
            from,
            to,
            type: s.type || 'standard',
            event_end_time: s.type === 'event' ? normalizeTime(s.event_end_time || s.to) : null,
            chosen: false,
            readonly: !!s.visit_id,
            invalidStudent: false,
            invalidTeacher: false,
            estimated_total: computeSlotTotal(from, to, tnorm || {})
          };
        });
      });
      setSlots(m);
      for (const tid of teacherIds) {
        await ensureTeacherWeekFree(tid, true);
      }
    } catch {
      // ignore
    }
  }
  useEffect(() => { loadExistingVisits(); }, [selectedStudent, selectedWeekStart]);

  const existingIntervalsByDate = useMemo(() => {
    const out = {};
    for (const { date } of weekDates) {
      const arr = (slots[date] || []).filter(s => s.visit_id).map(s => ({ from: s.from, to: s.to }));
      out[date] = mergeTouching(arr);
    }
    return out;
  }, [slots, weekDates]);

  const intersectionsByDate = useMemo(() => {
    const out = {};
    for (const { date } of weekDates) {
      const sFree = (studentAvailByDate[date]?.free || []).map(i => clampTo(i, dayStartM, dayEndM)).filter(Boolean);
      const tFree = (teacherAvailByDate[date]?.free || []).map(i => clampTo(i, dayStartM, dayEndM)).filter(Boolean);
      const inter = intersectTwoSets(sFree, tFree);
      const blocked = existingIntervalsByDate[date] || [];
      out[date] = subtractIntervals(inter, blocked);
    }
    return out;
  }, [weekDates, studentAvailByDate, teacherAvailByDate, existingIntervalsByDate]);

  function isPast(date) { return date < serverDateStr; }

  // --- Modal open + prefetch + Suggested matches + resolved_location ---
  async function openTeacherModal(date, from, to) {
    try {
      const res = await API.get(`/teacher-suggestions?student_id=${selectedStudent}&date=${date}&from=${from}&to=${to}&use_ors=${useORS?1:0}`);
      const raw = res.data || {};

      // prefetch teacher avail for week
      const ids = [];
      if (raw.default_teacher) {
        const idn = getTidNum(raw.default_teacher);
        if (idn != null) ids.push(idn);
      }
      (raw.alternatives || []).forEach(t => {
        const idn = getTidNum(t);
        if (idn != null) ids.push(idn);
      });
      await Promise.all(ids.map(id => ensureTeacherWeekFree(id)));

      const matchMap = await loadMatchMap(selectedStudent);

      const decorate = (t) => {
        if (!t) return null;
        const n = normalizeTeacher(t);
        const m = matchMap[getTid(n)] || null;
        return { ...n, _match: m };
      };

      const defaultT = decorate(raw.default_teacher);
      const alts = (raw.alternatives || [])
        .map(decorate)
        .sort((a,b) => (b?._match?.score ?? -Infinity) - (a?._match?.score ?? -Infinity));

      setTeacherOptions({
        ...raw,
        default_teacher: defaultT,
        alternatives: alts,
      });

      setModalDate(date);
      setModalFrom(from);
      setModalMaxTo(to);
      setIsEvent(false);
      setEventEndTime(to);
      setShowModal(true);
    } catch (e) {
      setStatus(e.response?.data?.error || 'Failed to load available teachers');
    }
  }

  // klik po student free/intersection segmentu
  function openFromStudentFreeClick(date, seg, e) {
    const list = slots[date] || [];
    const hasSavedForDay = list.some(s => s.visit_id);
    if (hasSavedForDay) {
      setStatus(`Day ${date} already has a saved visit. Delete it if you want to pick another.`);
      return;
    }

    const track = trackRefs.current[date];
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    let minuteAtX = dayStartM + Math.round(((clientX - rect.left) / rect.width) * spanM / SNAP_MIN) * SNAP_MIN;

    const segFromM = toMinutes(seg.from);
    const segToM   = toMinutes(seg.to);
    minuteAtX = Math.max(segFromM, Math.min(segToM - MIN_DURATION_MIN, minuteAtX));
    if (minuteAtX + MIN_DURATION_MIN > segToM) {
      minuteAtX = Math.max(segFromM, segToM - MIN_DURATION_MIN);
    }
    const start = minutesToHHMM(minuteAtX);

    openTeacherModal(date, start, seg.to);
  }

  // validacije
  function canSetEvent(date, from, to) {
    return withinStudentFree(date, from, to);
  }
  function canTeacherSetInterval(date, from, to, teacherId) {
    if (teacherId == null) return true;
    return withinTeacherFree(date, from, to, teacherId);
  }

  function recomputeValidity(date, item) {
    if (item.readonly || item.visit_id) return { invalidStudent: false, invalidTeacher: false };
    const invalidStudent = !withinStudentFree(date, item.from, item.to);
    const invalidTeacher = item.teacher ? !withinTeacherFree(date, item.from, item.to, getTidNum(item.teacher)) : false;
    return { invalidStudent, invalidTeacher };
  }

  function addLocalSlot(date, from, to, teacher, slotType = 'standard') {
    const tnorm = normalizeTeacher(teacher);
    const base = { from, to, teacher: tnorm, type: slotType };
    const { invalidStudent, invalidTeacher } = recomputeValidity(date, base);
    const m = getMatchForTeacher(tnorm);
    const tidNum = getTidNum(tnorm);

    setSlots(prev => {
      const copy = { ...prev };
      const list = (copy[date] || []).slice();
      list.push({
        from, to,
        teacher: tnorm,
        original_teacher_id: studentDefaultTeacherId,
        reassigned_status: tidNum === studentDefaultTeacherId ? 'none' : 'pending',
        reassigned_response_time: tidNum === studentDefaultTeacherId ? null : new Date().toISOString(),
        status: 'draft',
        visit_id: null,
        type: slotType,
        event_end_time: slotType === 'event' ? to : null,
        chosen: false,
        readonly: false,
        invalidStudent,
        invalidTeacher,
        estimated_total: computeSlotTotal(from, to, tnorm),
        match_score: m?.score ?? null,
        match_reasons: Array.isArray(m?.reasons) ? m.reasons : []
      });
      copy[date] = list;
      return copy;
    });
  }

  async function selectTeacherInModal(t) {
    const tnorm = normalizeTeacher(t);
    const tidNum = getTidNum(tnorm);
    const from = modalFrom;
    let toVal;
    let slotType = 'standard';

    if (isEvent && eventEndTime) {
      slotType = 'event';
      toVal = normalizeTime(eventEndTime);
      if (!canSetEvent(modalDate, from, toVal)) return;
      if (!canTeacherSetInterval(modalDate, from, toVal, tidNum)) return;
    } else {
      const end45 = addMinutes(from, MAX_STANDARD_MIN);
      const capped = minutesToHHMM(Math.min(toMinutes(end45), toMinutes(modalMaxTo)));
      toVal = (toMinutes(capped) - toMinutes(from) >= MIN_DURATION_MIN)
        ? capped
        : minutesToHHMM(Math.min(toMinutes(from) + MIN_DURATION_MIN, dayEndM));
      if (!canTeacherSetInterval(modalDate, from, toVal, tidNum)) return;
      if (!withinStudentFree(modalDate, from, toVal)) return;
    }

    if (toMinutes(toVal) <= toMinutes(from)) return;
    await ensureTeacherWeekFree(tidNum);
    addLocalSlot(modalDate, from, toVal, tnorm, slotType);
    setShowModal(false);
  }

  function setChosen(date, idx) {
    setSlots(prev => {
      const copy = { ...prev };
      const list = (copy[date] || []).map((s, i) => ({ ...s, chosen: i === idx }));
      copy[date] = list;
      return copy;
    });
  }

  // --- DELETE (saved + local) ---
  async function handleDeleteSlot(date, idx) {
    const item = (slots[date] || [])[idx];
    if (!item) return;

    if (item.visit_id) {
      const ok = window.confirm('Are you sure you want to permanently delete this SAVED and COMPLETED visit?');
      if (!ok) return;
      try {
        await API.delete(`/visits/${item.visit_id}`);
        setSlots(prev => {
          const copy = { ...prev };
          const list = (copy[date] || []).slice();
          list.splice(idx, 1);
          copy[date] = list;
          return copy;
        });
        setStatus('Visit deleted');

        const tid = getTidNum(item.teacher);
        if (tid != null) {
          await ensureTeacherWeekFree(tid, true);
          if (String(tid) === String(overlayTeacherId)) {
            await refreshOverlayTeacher();
          }
        }
      } catch {
        setStatus('Failed to delete visit');
      }
      return;
    }

    // lokalni kandidat
    setSlots(prev => {
      const copy = { ...prev };
      const list = (copy[date] || []).slice();
      list.splice(idx, 1);
      copy[date] = list;
      return copy;
    });
  }

  function validateBeforeSave() {
    for (const { date } of weekDates) {
      const list = slots[date] || [];
      if (!list.length) continue;

      const saved = list.filter(s => !!s.visit_id);
      const picked = list.filter(s => s.chosen && !s.visit_id);

      if (saved.length > 1) {
        return `Cannot save: ${date} already has ${saved.length} saved visits (allowed: 1 per day).`;
      }
      if (saved.length === 1 && picked.length > 0) {
        return `Cannot save on ${date}: a saved visit already exists and you also picked another. Keep exactly one visit per day (saved counts).`;
      }

      const hasDrafts = list.some(s => !s.visit_id);
      if (saved.length === 0 && hasDrafts) {
        if (picked.length !== 1) {
          return picked.length === 0
            ? `For ${date}, pick exactly one visit to save.`
            : `For ${date}, you picked more than one visit. Keep only one.`;
        }

        const s = picked[0];
        const dur = toMinutes(s.to) - toMinutes(s.from);
        if (s.invalidStudent) return `Chosen slot on ${date} ${s.from}-${s.to} is outside STUDENT availability.`;
        if (s.invalidTeacher) return `Chosen slot on ${date} ${s.from}-${s.to} is outside TEACHER availability.`;
        if (dur < MIN_DURATION_MIN) return `Chosen slot on ${date} must be at least ${MIN_DURATION_MIN} minutes.`;
        if (s.type !== 'event' && dur > MAX_STANDARD_MIN) {
          return `Standard visit on ${date} must be ≤ ${MAX_STANDARD_MIN} minutes (use Event for longer).`;
        }
      }
    }
    return '';
  }

  const preSaveError = useMemo(() => validateBeforeSave(), [slots, weekDates]);

  const hasAnythingToSave = useMemo(() => {
    for (const { date } of weekDates) {
      const list = slots[date] || [];
      if (list.some(s => s.visit_id)) continue;
      if (list.find(s => s.chosen && !s.visit_id)) return true;
    }
    return false;
  }, [slots, weekDates]);

  async function handleSave() {
    const err = validateBeforeSave();
    if (err) {
      setStatus(err);

      const m = err.match(/\d{4}-\d{2}-\d2/);
      // mali bug u originalu (regex), ostavljam tvoju logiku, možeš po želji popraviti na \d{4}-\d{2}-\d{2}
      if (m && trackRefs.current[m[0]]) {
        const el = trackRefs.current[m[0]];
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const oldOutline = el.style.outline;
        el.style.outline = '2px solid #ef4444';
        setTimeout(() => { if (el) el.style.outline = oldOutline || 'none'; }, 1500);
      }
      return;
    }

    const payload = [];
    for (const { date } of weekDates) {
      const list = slots[date] || [];
      if (list.some(s => s.visit_id)) continue;

      const chosen = list.find(s => s.chosen && s.teacher && !s.visit_id);
      if (!chosen) continue;

      payload.push({
        student_id: selectedStudent,
        teacher_id: getTidNum(chosen.teacher),
        scheduled_at: `${date} ${normalizeTime(chosen.from)}`,
        notes: '',
        visit_id: null,
        reassigned_status: chosen.reassigned_status || 'none',
        reassigned_response_time: chosen.reassigned_response_time || null,
        original_teacher_id:
          getTidNum(chosen.teacher) !== studentDefaultTeacherId ? studentDefaultTeacherId : getTidNum(chosen.teacher),
        status: chosen.status || 'draft',
        type: chosen.type || 'standard',
        event_end_time: normalizeTime(chosen.to)
      });
    }

    if (!payload.length) { setStatus('Nothing to save.'); return; }

    try {
      const res = await API.post('/visits/bulk', { visits: payload });
      if (!res.data.success) { setStatus('Save failed'); return; }
      setStatus('Visits saved');
      await loadExistingVisits();
      await refreshOverlayTeacher();
    } catch (e) {
      setStatus(e.response?.data?.error || 'Save failed');
    }
  }

  /** ---------- Drag & Resize ---------- */
  function startDrag(e, date, idx, mode) {
    const track = trackRefs.current[date];
    if (!track) return;
    const item = (slots[date] || [])[idx];
    if (!item || item.readonly || item.visit_id) return;

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    e.preventDefault();
    setDrag({
      date, idx, mode,
      startX: clientX,
      rectLeft: rect.left,
      rectWidth: rect.width,
      origFrom: item.from,
      origTo: item.to
    });
  }
  function onMove(e) {
    if (!drag) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const dx = clientX - drag.rectLeft;
    const minuteAtX = dayStartM + Math.round((dx / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;

    setSlots(prev => {
      const copy = { ...prev };
      const list = copy[drag.date].slice();
      const item = { ...list[drag.idx] };

      if (item.readonly || item.visit_id) return prev;

      const origDur = toMinutes(drag.origTo) - toMinutes(drag.origFrom);
      let newFrom = toMinutes(item.from);
      let newTo   = toMinutes(item.to);

      if (drag.mode === 'move') {
        const anchorAtStart = dayStartM + Math.round(((drag.startX - drag.rectLeft) / drag.rectWidth) * spanM / SNAP_MIN) * SNAP_MIN;
        const anchorOffset = toMinutes(drag.origFrom) - anchorAtStart;
        newFrom = Math.max(dayStartM, Math.min(dayEndM - origDur, minuteAtX + anchorOffset));
        newTo   = newFrom + origDur;
      } else if (drag.mode === 'resize-left') {
        const maxLeft = toMinutes(item.to) - MIN_DURATION_MIN;
        newFrom = Math.max(dayStartM, Math.min(maxLeft, minuteAtX));
      } else if (drag.mode === 'resize-right') {
        const minRight = toMinutes(item.from) + MIN_DURATION_MIN;
        newTo = Math.max(minRight, Math.min(dayEndM, minuteAtX));
      }

      if (item.type !== 'event' && (newTo - newFrom > MAX_STANDARD_MIN)) {
        if (drag.mode === 'resize-left') newFrom = newTo - MAX_STANDARD_MIN;
        else newTo = newFrom + MAX_STANDARD_MIN;
      }

      item.from = minutesToHHMM(newFrom);
      item.to   = minutesToHHMM(newTo);
      const validity = recomputeValidity(drag.date, item);
      item.invalidStudent = validity.invalidStudent;
      item.invalidTeacher = validity.invalidTeacher;
      item.estimated_total = computeSlotTotal(item.from, item.to, item.teacher);

      list[drag.idx] = item;
      copy[drag.date] = list;
      return copy;
    });
  }
  function endDrag() { setDrag(null); }

  useEffect(() => {
    function up(){ endDrag(); }
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up, { passive: false });
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
    };
  }, [drag]);

  /** ---------- UI ---------- */
  return (
    <div className="p-2 max-w-7xl mx-auto">
      <Toast text={status} onClose={()=>setStatus('')} />

      <h2 className="text-lg font-bold mb-4">Smart Scheduler (drag, multi-teacher, 1/day final)</h2>

      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end mb-4">
        <div>
          <label className="block mb-1">Student:</label>
          <select className="border px-2 py-1 w-64" value={selectedStudent} onChange={e=>setSelectedStudent(e.target.value)}>
            <option value="">-- Choose student --</option>
            {students
              .slice()
              .sort(
                (a,b)=>
                  (a.last_name||'').localeCompare(b.last_name||'') ||
                  (a.first_name||'').localeCompare(b.first_name||'')
              )
              .map(s=>(
                <option key={s.id} value={s.id}>
                  {(s.last_name||'')+', '+(s.first_name||'')}
                </option>
              ))}
          </select>
        </div>

        {!!selectedStudent && (
          <>
            <div>
              <label className="block mb-1">Week:</label>
              <select className="border px-2 py-1 w-56" value={selectedWeekStart} onChange={e=>setSelectedWeekStart(e.target.value)}>
                <option value="">-- Choose week --</option>
                {weekOptions.map((w,i)=><option key={i} value={w.value}>{w.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block mb-1">Overlay teacher:</label>
              <select className="border px-2 py-1 w-64" value={overlayTeacherId} onChange={e=>setOverlayTeacherId(e.target.value)}>
                <option value="">{studentDefaultTeacherId ? '(use default)' : '-- choose --'}</option>
                {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {studentDefaultTeacherId && !overlayTeacherId && (
                <div className="text-xs text-gray-500 mt-1">Using default teacher of selected student.</div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Travel cost (slower)</span>
              <button
                type="button"
                className={
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors " +
                  (useORS ? "bg-blue-600" : "bg-gray-300")
                }
                onClick={()=>setUseORS(v=>!v)}
              >
                <span
                  className={
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform " +
                    (useORS ? "translate-x-6" : "translate-x-1")
                  }
                />
              </button>
              <span className="text-xs text-gray-400">{useORS ? 'ON' : 'OFF'}</span>
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-4 text-sm mb-3">
        <span className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-green-300" /> Student free</span>
        <span className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-blue-300" /> Teacher free</span>
        <span className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-teal-400" /> Intersection (click to add)</span>
        <span className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-gray-300 border" /> Candidate (drag / resize)</span>
      </div>

      {weekDates.length > 0 && (
        <div className="space-y-4">
          {weekDates.map(({date, dayName})=>{
            const sFree = (studentAvailByDate[date]?.free || []).map(i => clampTo(i, dayStartM, dayEndM)).filter(Boolean);
            const tFree = (teacherAvailByDate[date]?.free || []).map(i => clampTo(i, dayStartM, dayEndM)).filter(Boolean);
            const inter = intersectionsByDate[date] || [];
            const daySlots = slots[date] || [];
            const isPastDay = isPast(date);

            const savedCount = daySlots.filter(s=>!!s.visit_id).length;
            const pickedCount = daySlots.filter(s=>s.chosen && !s.visit_id).length;
            const draftsCount = daySlots.filter(s=>!s.visit_id).length;
            const hasSavedForDay = savedCount > 0;

            let dayMsg = '';
            let dayMsgColor = 'text-gray-600';
            if (savedCount > 1) {
              dayMsg = `Already has ${savedCount} saved visits (allowed: 1).`;
              dayMsgColor = 'text-red-600';
            } else if (savedCount === 1 && draftsCount > 0) {
              dayMsg = 'A saved visit exists — new candidates will not be saved unless you delete the saved one.';
              dayMsgColor = 'text-orange-600';
            } else if (savedCount === 0 && draftsCount > 0) {
              if (pickedCount === 0) { dayMsg = 'Pick exactly one candidate to save.'; dayMsgColor = 'text-gray-600'; }
              if (pickedCount > 1)  { dayMsg = 'Too many picks — keep only one.'; dayMsgColor = 'text-red-600'; }
            }

            return (
              <div key={date} className="border rounded p-3 shadow-sm">
                <div className={`font-semibold mb-2 px-2 py-1 rounded flex items-center gap-2 ${isPastDay ? 'text-gray-400 bg-gray-50' : 'text-gray-800 bg-gray-100'}`}>
                  <span>{dayName} – {date}</span>
                  {savedCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                      Saved {savedCount}
                    </span>
                  )}
                  {pickedCount > 1 && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">
                      Multiple picks
                    </span>
                  )}
                  {savedCount === 0 && draftsCount > 0 && pickedCount === 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-gray-50 text-gray-700 border-gray-200">
                      Pick 1
                    </span>
                  )}
                  {savedCount === 1 && draftsCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                      Saved exists
                    </span>
                  )}
                </div>

                <div
                  className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                  ref={el => (trackRefs.current[date] = el)}
                  data-date={date}
                >
                  {/* satni tick-ovi */}
                  {Array.from({length: (spanM/60)+1}).map((_,i)=>{
                    const leftPct = (i*60)/spanM*100;
                    return (
                      <div key={i}>
                        <div className="absolute top-4 bottom-0 border-l border-gray-300" style={{ left: `${leftPct}%` }} />
                        <div className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2" style={{ left: `${leftPct}%` }}>
                          {minutesToHHMM(dayStartM + i*60).slice(0,2)}
                        </div>
                      </div>
                    );
                  })}

                  {/* student free */}
                  {sFree.map((seg,idx)=>{
                    const box = pctBox(seg);
                    const clickable = !isPastDay && !hasSavedForDay;
                    return clickable ? (
                      <button
                        key={'s'+idx}
                        className="absolute top-4 bottom-1 bg-green-300 opacity-50 rounded hover:opacity-60"
                        style={{left:box.left,width:box.width}}
                        title={`Student free ${seg.from}–${seg.to} (click to choose start time)`}
                        onClick={(e)=>openFromStudentFreeClick(date, seg, e)}
                      />
                    ) : (
                      <div
                        key={'s'+idx}
                        className={`absolute top-4 bottom-1 bg-green-300 ${hasSavedForDay ? 'opacity-30' : 'opacity-50'} rounded`}
                        style={{left:box.left,width:box.width}}
                        title={hasSavedForDay ? 'This day already has a saved visit' : undefined}
                      />
                    );
                  })}

                  {/* teacher free */}
                  {tFree.map((seg,idx)=>{
                    const box = pctBox(seg);
                    return (
                      <div
                        key={'t'+idx}
                        className="absolute top-4 bottom-1 bg-blue-300 opacity-40 rounded"
                        style={{left:box.left,width:box.width}}
                      />
                    );
                  })}

                  {/* intersections */}
                  {!isPastDay && inter.map((seg,idx)=>{
                    const box = pctBox(seg);
                    const clickable = !hasSavedForDay;
                    return clickable ? (
                      <button
                        key={'i'+idx}
                        className="absolute top-4 bottom-1 bg-teal-400/70 hover:bg-teal-400 rounded"
                        style={{left:box.left,width:box.width}}
                        title={`Intersection ${seg.from}–${seg.to} (click to choose start time)`}
                        onClick={(e)=>openFromStudentFreeClick(date, seg, e)}
                      />
                    ) : (
                      <div
                        key={'i'+idx}
                        className="absolute top-4 bottom-1 bg-teal-400/50 rounded opacity-40"
                        style={{left:box.left,width:box.width}}
                        title="Saved visit exists — adding new candidates is disabled"
                      />
                    );
                  })}

                  {/* kandidati + saved */}
                  {daySlots.map((s, idx)=>{
                    const box = pctBox(s);
                    const color = colorForTeacher(getTid(s.teacher));
                    const anyInvalid = (s.readonly || s.visit_id) ? false : (s.invalidStudent || s.invalidTeacher);
                    const border = (s.readonly || s.visit_id)
                      ? 'border-emerald-700'
                      : (anyInvalid ? 'border-red-500' : (s.chosen ? 'border-emerald-700' : 'border-gray-400'));
                    const opacity = (s.readonly || s.visit_id)
                      ? 'opacity-100'
                      : (anyInvalid ? 'opacity-70' : 'opacity-90');
                    return (
                      <div
                        key={'c'+idx}
                        className={`absolute top-5 bottom-2 ${opacity} rounded border ${border} shadow-sm overflow-hidden`}
                        style={{left:box.left,width:box.width, background: 'white'}}
                      >
                        <div className={`${color} h-full opacity-40`} />
                        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                          <span className="font-mono">{s.from}–{s.to}</span>
                          <span className="hidden sm:inline font-semibold">{s.teacher?.name || '—'}</span>
                          <span className="sm:hidden font-semibold">{initials(s.teacher?.name || '')}</span>
                        </div>

                        {!isPastDay && (
                          <button
                            className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                            title="Delete"
                            onClick={() => handleDeleteSlot(date, idx)}
                          >×</button>
                        )}

                        {!isPastDay && !(s.readonly || s.visit_id) && (
                          <>
                            <div
                              className="absolute inset-0 cursor-grab"
                              onMouseDown={(e)=>startDrag(e,date,idx,'move')}
                              onTouchStart={(e)=>startDrag(e,date,idx,'move')}
                            />
                            <div
                              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize"
                              onMouseDown={(e)=>startDrag(e,date,idx,'resize-left')}
                              onTouchStart={(e)=>startDrag(e,date,idx,'resize-left')}
                            />
                            <div
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize"
                              onMouseDown={(e)=>startDrag(e,date,idx,'resize-right')}
                              onTouchStart={(e)=>startDrag(e,date,idx,'resize-right')}
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!!dayMsg && (
                  <div className={`mt-1 text-xs ${dayMsgColor}`}>{dayMsg}</div>
                )}

                {(daySlots.length > 0) && (
                  <div className="mt-2 space-y-1">
                    {daySlots.map((s, idx)=>(
                      <div key={'l'+idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-block w-3 h-3 rounded-full ${colorForTeacher(getTid(s.teacher))}`} />
                          <span className="font-semibold">{s.teacher?.name || '—'}</span>
                          <span className="px-2 py-0.5 rounded bg-gray-100 border text-xs">
                            {typeof s.estimated_total === 'number' ? `$${s.estimated_total.toFixed(2)}` : '—'}
                          </span>
                          <span className="font-mono">{s.from}–{s.to}</span>

                          <TypeBadge type={s.type} />
                          <StatusBadge status={s.status || 'draft'} />
                          <ReassignedBadge reassigned={s.reassigned_status} />

                          {!(s.readonly || s.visit_id) && s.invalidStudent && (
                            <span className="text-red-600 ml-1">
                              outside <b>student</b> availability
                            </span>
                          )}
                          {!(s.readonly || s.visit_id) && s.invalidTeacher && (
                            <span className="text-orange-600 ml-1">
                              outside <b>teacher</b> availability
                            </span>
                          )}
                          {(s.readonly || s.visit_id) && (
                            <span className="ml-1 text-emerald-700 text-xs font-semibold">
                              saved
                            </span>
                          )}

                          {typeof s.match_score === 'number' && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs">
                              Match {s.match_score}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!(s.readonly || s.visit_id) ? (
                            <button
                              className={
                                `px-2 py-0.5 rounded border ` +
                                (s.chosen ? 'bg-emerald-600 text-white' : 'bg-white') +
                                ((s.invalidStudent||s.invalidTeacher) ? ' opacity-50 cursor-not-allowed' : '')
                              }
                              onClick={() => !(s.invalidStudent||s.invalidTeacher) && setChosen(date, idx)}
                              disabled={s.invalidStudent||s.invalidTeacher}
                              title="Pick this teacher for the day"
                            >
                              {s.chosen ? 'Picked' : 'Pick'}
                            </button>
                          ) : (
                            <span className="px-2 py-0.5 rounded border bg-gray-50 text-gray-500 cursor-not-allowed">
                              Saved
                            </span>
                          )}

                          <button
                            className={
                              `px-2 py-0.5 rounded border text-red-600 ` +
                              (isPast(date) ? 'opacity-50 cursor-not-allowed' : '')
                            }
                            onClick={()=>!isPast(date) && handleDeleteSlot(date, idx)}
                            disabled={isPast(date)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedWeekStart && (
        <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:items-center">
          <button
            onClick={handleSave}
            className={
              `px-4 py-2 rounded text-white ` +
              ((!hasAnythingToSave || preSaveError)
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700')
            }
            disabled={!hasAnythingToSave || !!preSaveError}
            title={!hasAnythingToSave ? 'No picked visits to save' : (preSaveError || '')}
          >
            Save (1 final per day)
          </button>

          {!hasAnythingToSave && (
            <div className="text-sm text-gray-600">
              Nothing to save — pick exactly one candidate on the day you want to save.
            </div>
          )}
          {!!preSaveError && (
            <div className="text-sm text-red-600">
              {preSaveError}
            </div>
          )}
        </div>
      )}

      {/* Modal: Select Teacher */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={()=>setShowModal(false)}
            >
              ×
            </button>
            <h3 className="text-lg font-bold mb-2">Select Teacher</h3>

            {/* info o lokaciji studenta za ovaj predlog */}
            {teacherOptions.resolved_location && (
              <div className="text-xs text-gray-700 mb-2">
                Using student{' '}
                <b>
                  {teacherOptions.resolved_location === 'alt'
                    ? 'ALT address'
                    : 'PRIMARY address'}
                </b>{' '}
                for travel distance calculation.
              </div>
            )}

            {/* ORS status hintovi */}
            {teacherOptions.orsMatrixStatus === 'SKIPPED' && (
              <div className="text-blue-700 text-xs mb-2">
                <b>Travel cost calculation skipped</b> for faster loading.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'FAILED' && (
              <div className="text-red-700 text-xs mb-2">
                <b>Travel cost calculation failed</b> – showing estimated costs only.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'NO_STUDENT_COORD' && (
              <div className="text-yellow-700 text-xs mb-2">
                Cannot calculate travel cost – student has no valid coordinates for this slot.
              </div>
            )}
            {teacherOptions.orsMatrixStatus === 'NO_CANDIDATES' && (
              <div className="text-yellow-700 text-xs mb-2">
                No teachers with known locations for travel cost matrix – showing hourly+bonus only.
              </div>
            )}

            {/* Event toggle */}
		<label className="flex items-center gap-2 mt-2 mb-2">
		  <input
		    type="checkbox"
		    checked={isEvent}
		    onChange={e => {
		      setIsEvent(e.target.checked);
		      if (!e.target.checked) setEventEndTime('');
		    }}
		  />

		  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
		    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
		      PI
		    </span>
			<span className="text-xs sm:text-sm">or</span>
		    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
		      Event visit (multi-hour)
		    </span>
		  </div>
		</label>
            {isEvent && (
              <div className="flex items-center gap-2 mb-2">
                <label>End time:</label>
                <input
                  type="time"
                  value={eventEndTime}
                  min={modalFrom}
                  max="23:59"
                  onChange={e => setEventEndTime(e.target.value)}
                  className="border px-2 py-1"
                />
                {eventEndTime && !canSetEvent(modalDate, modalFrom, eventEndTime) && (
                  <span className="text-red-500 text-xs ml-2">
                    Student not available for this interval!
                  </span>
                )}
              </div>
            )}

            {/* Default teacher blok */}
            {teacherOptions.default_teacher && (() => {
              const t = teacherOptions.default_teacher;
              const tidNum = getTidNum(t);

              const standardEnd45 = addMinutes(modalFrom, MAX_STANDARD_MIN);
              const standardIntendedTo =
                minutesToHHMM(Math.min(toMinutes(standardEnd45), toMinutes(modalMaxTo)));
              const okStudentStd = !isEvent
                ? withinStudentFree(modalDate, modalFrom, standardIntendedTo)
                : true;
              const okTeacherStd = !isEvent
                ? canTeacherSetInterval(modalDate, modalFrom, standardIntendedTo, tidNum)
                : true;

              const showStdWarnings = !isEvent && (!okStudentStd || !okTeacherStd);
              const showEventTeacherWarn =
                isEvent &&
                eventEndTime &&
                !canTeacherSetInterval(modalDate, modalFrom, eventEndTime, tidNum);

              const teacherHintStd = !isEvent && !okTeacherStd
                ? teacherCoverageHint(modalDate, tidNum, modalFrom, standardIntendedTo)
                : '';
              const studentHintStd = !isEvent && !okStudentStd
                ? studentCoverageHint(modalDate, modalFrom, standardIntendedTo)
                : '';

              return (
                <div className="mb-4 border p-2 rounded bg-green-100">
                  <div className="font-semibold">Default Teacher</div>
                  <div className="text-sm mt-1">
                    <b>Hourly:</b> ${t.hourly_rate?.toFixed?.(2) ?? '—'}<br />
                    <b>Travel:</b>{' '}
                    {typeof t.distance_miles === "number"
                      ? `${t.distance_miles.toFixed(2)} mi × $${t.travel_cost_per_mile?.toFixed?.(2) ?? '—'}/mi = $${t.travel_cost?.toFixed?.(2) ?? '—'}`
                      : <span className="text-xs text-gray-500">Not calculated</span>}
                    <br />
                    <b>Bonus:</b> ${t.bonus?.toFixed?.(2) ?? '—'}<br />
                    <b>Total: ${t.total_cost?.toFixed?.(2) ?? '—'}</b><br />
                    <span className="text-xs text-gray-500">{t.cost_breakdown}</span>
                  </div>

                  {t._match && (
                    <div className="mt-2">
                      <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
                        Match score: {t._match.score}
                      </span>
                      {!!t._match.reasons?.length && (
                        <ul className="list-disc ml-5 mt-1 text-xs text-gray-700">
                          {t._match.reasons.slice(0,8).map((r,i)=><li key={i}>{r}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  {isEvent && eventEndTime && showEventTeacherWarn && (
                    <div className="text-red-600 text-xs mt-1">
                      {teacherCoverageHint(modalDate, tidNum, modalFrom, eventEndTime)}
                    </div>
                  )}
                  {showStdWarnings && (
                    <div className="text-red-600 text-xs mt-1">
                      {!okTeacherStd && <div>{teacherHintStd}</div>}
                      {!okStudentStd && <div>Student: {studentHintStd}</div>}
                    </div>
                  )}

                  <button
                    className="mt-2 bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded"
                    onClick={() => {
                      if (isEvent) {
                        if (!eventEndTime || !canSetEvent(modalDate, modalFrom, eventEndTime)) return;
                        if (!canTeacherSetInterval(modalDate, modalFrom, eventEndTime, tidNum)) return;
                      } else {
                        if (!okTeacherStd || !okStudentStd) return;
                      }
                      selectTeacherInModal(t);
                    }}
                    disabled={
                      (isEvent && (!eventEndTime || !canSetEvent(modalDate, modalFrom, eventEndTime) || showEventTeacherWarn)) ||
                      (!isEvent && (!okTeacherStd || !okStudentStd))
                    }
                  >
                    {t.name}
                  </button>
                </div>
              );
            })()}

            {/* Alternatives */}
            {teacherOptions.alternatives?.length > 0 ? (
              teacherOptions.alternatives.map(t=>{
                const tidNum = getTidNum(t);
                const standardEnd45 = addMinutes(modalFrom, MAX_STANDARD_MIN);
                const standardIntendedTo =
                  minutesToHHMM(Math.min(toMinutes(standardEnd45), toMinutes(modalMaxTo)));
                const okStudentStd = !isEvent
                  ? withinStudentFree(modalDate, modalFrom, standardIntendedTo)
                  : true;
                const okTeacherStd = !isEvent
                  ? canTeacherSetInterval(modalDate, modalFrom, standardIntendedTo, tidNum)
                  : true;
                const showStdWarnings = !isEvent && (!okStudentStd || !okTeacherStd);
                const showEventTeacherWarn =
                  isEvent &&
                  eventEndTime &&
                  !canTeacherSetInterval(modalDate, modalFrom, eventEndTime, tidNum);

                const teacherHintStd = !isEvent && !okTeacherStd
                  ? teacherCoverageHint(modalDate, tidNum, modalFrom, standardIntendedTo)
                  : '';
                const studentHintStd = !isEvent && !okStudentStd
                  ? studentCoverageHint(modalDate, modalFrom, standardIntendedTo)
                  : '';

                return (
                  <div key={getTid(t)} className="mb-2 border p-2 rounded bg-yellow-100">
                    <div className="font-semibold">{t.name}</div>
                    <div className="text-sm mt-1">
                      <b>Hourly:</b> ${t.hourly_rate?.toFixed?.(2) ?? '—'}<br />
                      <b>Travel:</b>{' '}
                      {t.travel_cost_unknown ? (
                        <span className="text-xs text-yellow-700 font-semibold">
                          Travel cost not calculated – teacher has no prior visits that day or matrix unavailable
                        </span>
                      ) : typeof t.distance_miles === "number" ? (
                        <span>
                          {t.distance_miles.toFixed(2)} mi × ${t.travel_cost_per_mile?.toFixed?.(2) ?? '—'}/mi = ${t.travel_cost?.toFixed?.(2) ?? '—'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">Not calculated</span>
                      )}
                      <br />
                      <b>Bonus:</b> ${t.bonus?.toFixed?.(2) ?? '—'}<br />
                      <b>Total: ${t.total_cost?.toFixed?.(2) ?? '—'}</b><br />
                      <span className="text-xs text-gray-500">{t.cost_breakdown}</span>
                    </div>

                    {t._match && (
                      <div className="mt-2">
                        <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold">
                          Match score: {t._match.score}
                        </span>
                        {!!t._match.reasons?.length && (
                          <ul className="list-disc ml-5 mt-1 text-xs text-gray-700">
                            {t._match.reasons.slice(0,8).map((r,i)=><li key={i}>{r}</li>)}
                          </ul>
                        )}
                      </div>
                    )}

                    {isEvent && eventEndTime && showEventTeacherWarn && (
                      <div className="text-red-600 text-xs mt-1">
                        {teacherCoverageHint(modalDate, tidNum, modalFrom, eventEndTime)}
                      </div>
                    )}
                    {showStdWarnings && (
                      <div className="text-red-600 text-xs mt-1">
                        {!okTeacherStd && <div>{teacherHintStd}</div>}
                        {!okStudentStd && <div>Student: {studentHintStd}</div>}
                      </div>
                    )}

                    <button
                      className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                      onClick={() => {
                        if (isEvent) {
                          if (!eventEndTime || !canSetEvent(modalDate, modalFrom, eventEndTime)) return;
                          if (!canTeacherSetInterval(modalDate, modalFrom, eventEndTime, tidNum)) return;
                        } else {
                          if (!okTeacherStd || !okStudentStd) return;
                        }
                        selectTeacherInModal(t);
                      }}
                      disabled={
                        (isEvent && (!eventEndTime || !canSetEvent(modalDate, modalFrom, eventEndTime) || showEventTeacherWarn)) ||
                        (!isEvent && (!okTeacherStd || !okStudentStd))
                      }
                    >
                      Select
                    </button>
                  </div>
                );
              })
            ) : (
              <p>No alternative teachers available.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


